import { Button, Icon, IconSet, Layout, toast } from '@postscript/components';
import { useQuery } from '@tanstack/react-query';
import { FF_IT_MESSAGE_FEEDBACK } from 'components/admin/utils/feature-flags';
import { FeedbackPanel, FeedbackThumbs } from 'components/feedback';
import {
  FeedbackSource,
  FeedbackType as UnifiedFeedbackType,
  MessageType,
} from 'components/feedback/hooks/types';
import { useSubmitFeedback as useUnifiedSubmitFeedback } from 'components/feedback/hooks/useSubmitFeedback';
import {
  DEFAULT_CATEGORY_LABELS,
  REASON_SETS_BY_MESSAGE_TYPE,
} from 'components/feedback/types';
import { useFlowCampaignViewResolver } from 'components/shopperCampaigns/hooks/useFlowCampaignViewResolver';
import { FlowCampaignView } from 'components/shopperCampaigns/utils/flowViewPath';
import { usePSLabs } from 'controllers/contexts/labsFeatures';
import { api } from 'controllers/network/apiClient';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { getSupportIntegrationLabel } from '../constants';
import { StyledMetadata } from '../styles/message';
import { Message, Subscriber } from '../types';
import {
  isITMessage,
  isShopperAutomationMessage,
  isShopperAutomationSource,
  isShopperCampaignMessage,
  isShopperMessage,
} from './messageGuards';
import {
  deriveFeedbackDisplay,
  FeedbackDisplay,
  useMessageFeedback,
} from './useMessageFeedback';

export { isITMessage } from './messageGuards';

interface MessageMetadataProps {
  message: Message;
  showTimestamp?: boolean;
  subscriber: Subscriber | null;
  onOpenDetails?: () => void;
}

interface MessageSourceProps {
  message: Message;
  showTimestamp: boolean;
  subscriber: Subscriber | null;
  onOpenDetails?: () => void;
}

const getOutboundSourceLabel = (message: Message): string | null => {
  if (message.direction !== 'outgoing') return null;
  if (!message.type) return null;

  switch (message.type) {
    case 'campaign':
      return message.type_id != null ? 'View Campaign' : null;
    case 'keyword':
      return message.type_id != null ? 'View Keyword' : null;
    case 'sent_automation_message':
      return 'Automation message';
    case 'flow_action':
      return message.type_id != null ? 'View Flow' : null;
    case 'opt-out':
      return 'Opt-out message';
    case 'ooi-winback':
      return 'Winback message';
    case 'reply':
      return !message.subtype ? 'Manual Reply message' : null;
    case 'opt-in':
    case 'double-opt-in':
    case 'opt-in-one-time-passcode':
    case 'onthread-opt-in':
      return 'Opt-in message';
    case 'desktop-popup':
    case 'mobile-popup':
    case 'desktop-block-popup':
    case 'mobile-block-popup':
    case 'desktop-bis-popup':
    case 'mobile-bis-popup':
      return 'Popup message';
    case 'desktop-landing-page':
    case 'mobile-landing-page':
      return 'Landing Page message';
    default:
      return null;
  }
};

const MetaRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-1);
`;

const ClickableIcon = styled.span`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
`;

// The "open Message Details" affordance — mirrors the FeedbackThumbs button
// shape (text/monochrome/small icon-only) so it sits inline with the thumbs
// without changing baseline. We let the default text-button hover handle the
// color change, which matches the feedback icons' hover behavior minus the
// green/red tint.
const DetailsIconButton = styled(Button)`
  padding: 0;
  min-width: 24px;
  min-height: 24px;
`;

const SourceLabel = styled.a<{ $isLoading?: boolean }>`
  color: var(--link-color);
  font-weight: bold;
  cursor: pointer;
  text-decoration: none;
  max-width: 30ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    color: ${(props) =>
      props.$isLoading ? 'var(--link-color)' : 'var(--link-color-hover)'};
    text-decoration: ${(props) => (props.$isLoading ? 'none' : 'underline')};
  }
`;

const StaticLabel = styled.span`
  max-width: 30ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

interface FlowRouteResponse {
  flow: {
    guid: string;
    id?: number;
    name: string;
    type: 'AUTOMATION' | 'CAMPAIGN';
    collectionId?: number;
    isHidden?: boolean;
  };
  path: string;
}

interface KeywordResponse {
  keyword: {
    keyword: string;
  };
}

const MessageSource = ({
  message,
  showTimestamp,
  subscriber,
  onOpenDetails,
}: MessageSourceProps): JSX.Element | null => {
  const isAxiosLikeError = (
    err: unknown,
  ): err is { response?: { status?: number } } => {
    if (typeof err !== 'object' || err === null) return false;
    const maybeResp = (err as { response?: unknown }).response;
    if (typeof maybeResp !== 'object' || maybeResp === null) return false;
    const { status } = maybeResp as { status?: unknown };
    return typeof status === 'number' || status === undefined;
  };

  const { mutate: submitUnifiedFeedback } = useUnifiedSubmitFeedback();

  const {
    canSubmitFeedback,
    feedbackDisplay,
    isLocked,
    showFeedbackPanel,
    panelFeedback,
    // Cross-shop scoping shared with the standard handlers, reused by the IT
    // submit path below.
    shopId,
    handleThumbsUp,
    handleThumbsDown,
    handleFeedbackPanelSubmit,
    handleFeedbackPanelClose,
  } = useMessageFeedback(message, subscriber);

  const { hasLabsFlag } = usePSLabs();
  const itFeedbackEnabled = hasLabsFlag(FF_IT_MESSAGE_FEEDBACK);

  const resolveFlowView = useFlowCampaignViewResolver();
  const isShopperCampaign = isShopperCampaignMessage(message);

  // Route hidden collections (Shopper Campaign send flows) through the same
  // shared helper as every other surface, so the destination and the
  // "Campaign message" label can't drift. The shopper-campaigns subtype is
  // treated as hidden too, covering flow responses that predate isHidden.
  // Visible flows keep the API's path untouched.
  const resolveFlowRoute = (
    data?: FlowRouteResponse,
  ): FlowCampaignView | null => {
    if (!data) return null;
    if (!data.flow.isHidden && !isShopperCampaign) {
      return { path: data.path, isCampaignView: false };
    }
    const view = resolveFlowView({
      guid: data.flow.guid,
      flowCollectionId: data.flow.collectionId,
      isHidden: true,
    });
    if (!data.flow.isHidden && !view.isCampaignView) {
      return { path: data.path, isCampaignView: false };
    }
    return view;
  };

  const {
    data: flowRouteData,
    isLoading: isFlowRouteLoading,
    error: flowRouteError,
  } = useQuery({
    queryKey: ['flowRoute', message.type_id ?? ''],
    queryFn: () =>
      api.get<FlowRouteResponse>(
        `/v2/flowbuilder/flow_actions/${message.type_id}/flow/`,
      ),
    enabled: message.type === 'flow_action' && message.type_id != null,
    staleTime: Infinity,
    retry: false,
  });
  const hasShopperAutomationSource = isShopperAutomationSource(message);
  const shouldShowShopperResponses =
    isShopperMessage(message) && !hasShopperAutomationSource;

  const { data: itFlowRouteData, isLoading: isITFlowRouteLoading } = useQuery({
    queryKey: ['itFlowRoute', message.flowActionId ?? ''],
    queryFn: () =>
      api.get<FlowRouteResponse>(
        `/v2/flowbuilder/flow_actions/${message.flowActionId}/flow/`,
      ),
    enabled:
      itFeedbackEnabled &&
      isITMessage(message) &&
      !isShopperAutomationMessage(message) &&
      message.flowActionId != null,
    staleTime: Infinity,
    retry: false,
  });

  const { data: keywordData, isLoading: isKeywordLoading } = useQuery({
    queryKey: ['keyword', message.type_id ?? ''],
    queryFn: () => api.get<KeywordResponse>(`/keywords/${message.type_id}`),
    enabled: message.type === 'keyword' && message.type_id != null,
    staleTime: Infinity,
    retry: false,
  });

  const flowRoute = resolveFlowRoute(flowRouteData);

  const sourceHref = (() => {
    if (!message.type || message.type_id == null) return null;
    switch (message.type) {
      case 'campaign':
        return `/campaigns/${message.type_id}`;
      case 'keyword':
        return `/keywords/${message.type_id}`;
      case 'flow_action':
        return flowRoute?.path ?? null;
      default:
        return null;
    }
  })();

  // IT feedback state/handlers below are declared unconditionally to satisfy the
  // Rules of Hooks; they are only reached when itFeedbackEnabled is true.

  const [itShowFeedbackPanel, setITShowFeedbackPanel] = useState(false);
  const [itOptimisticFeedback, setITOptimisticFeedback] =
    useState<FeedbackDisplay | null>(null);

  const itApiFeedback = isITMessage(message)
    ? deriveFeedbackDisplay(message)
    : '';
  const itIsLocked = itApiFeedback !== '' || itOptimisticFeedback !== null;

  useEffect(() => {
    setITOptimisticFeedback(null);
  }, [itApiFeedback]);

  const itFeedbackDisplay: FeedbackDisplay =
    itOptimisticFeedback ??
    (itShowFeedbackPanel && itApiFeedback !== 'NEGATIVE'
      ? 'NEGATIVE'
      : itApiFeedback);

  const itPanelFeedback =
    message.feedbackSummary?.feedbackType === 'thumbs_down'
      ? message.feedbackSummary
      : null;

  const itCategories = REASON_SETS_BY_MESSAGE_TYPE[
    MessageType.INFINITY_TESTING
  ].reduce<Record<string, string>>((acc, key) => {
    if (key in DEFAULT_CATEGORY_LABELS) {
      return {
        ...acc,
        [key]:
          DEFAULT_CATEGORY_LABELS[key as keyof typeof DEFAULT_CATEGORY_LABELS],
      };
    }
    return acc;
  }, {});

  const handleITThumbsUp = () => {
    if (itIsLocked) return;
    submitUnifiedFeedback({
      messageType: MessageType.INFINITY_TESTING,
      feedbackSource: FeedbackSource.CONVERSATIONS_VIEW,
      feedbackType: UnifiedFeedbackType.THUMBS_UP,
      messageId: message.sent_message_id,
      typeId: message.flowActionId,
      subscriberId: subscriber?.subscriber.id,
      shopId,
    });
    setITOptimisticFeedback('POSITIVE');
    setITShowFeedbackPanel(false);
  };

  const handleITThumbsDown = () => {
    if (itIsLocked) return;
    setITShowFeedbackPanel(!itShowFeedbackPanel);
  };

  const handleITFeedbackPanelSubmit = (
    category: string,
    feedbackText: string,
  ) => {
    submitUnifiedFeedback({
      messageType: MessageType.INFINITY_TESTING,
      feedbackSource: FeedbackSource.CONVERSATIONS_VIEW,
      feedbackType: UnifiedFeedbackType.THUMBS_DOWN,
      messageId: message.sent_message_id,
      typeId: message.flowActionId,
      subscriberId: subscriber?.subscriber.id,
      shopId,
      feedbackValue: feedbackText || undefined,
      feedbackMetadata: category ? { selectedPills: [category] } : undefined,
    });
    setITOptimisticFeedback('NEGATIVE');
  };

  const handleITFeedbackPanelClose = () => {
    setITShowFeedbackPanel(false);
  };

  const handleSourceClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (!sourceHref) {
        e.preventDefault();
        if (message.type === 'flow_action' && flowRouteError) {
          if (
            isAxiosLikeError(flowRouteError) &&
            flowRouteError.response?.status === 404
          ) {
            toast.error('Flow not found');
          } else {
            toast.error('Unable to load flow');
          }
        }
      }
    },
    [sourceHref, flowRouteError, message.type],
  );

  if (
    itFeedbackEnabled &&
    isITMessage(message) &&
    !isShopperAutomationMessage(message)
  ) {
    const itFlowHref = resolveFlowRoute(itFlowRouteData)?.path ?? null;
    const itFlowName = itFlowRouteData?.flow.name;
    const itDisplayLabel = isITFlowRouteLoading
      ? 'Loading...'
      : itFlowName ?? 'View Flow';

    return (
      <div>
        <Layout align="justify">
          <FeedbackThumbs
            feedback={itFeedbackDisplay}
            disabled={itIsLocked}
            onThumbsUp={handleITThumbsUp}
            onThumbsDown={handleITThumbsDown}
          />
          <MetaRow>
            <StaticLabel>Infinity Test</StaticLabel>
            {itFlowHref ? (
              <>
                {' • '}
                <ClickableIcon
                  onClick={() =>
                    window.open(itFlowHref, '_blank', 'noopener,noreferrer')
                  }
                >
                  <Icon component={IconSet.ArrowUpRight} size="small" />
                </ClickableIcon>
                <SourceLabel
                  href={itFlowHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  $isLoading={isITFlowRouteLoading}
                  title={itFlowName}
                >
                  {itDisplayLabel}
                </SourceLabel>
              </>
            ) : (
              !isITFlowRouteLoading && (
                <>
                  {' • '}
                  <StaticLabel>{itDisplayLabel}</StaticLabel>
                </>
              )
            )}
            {showTimestamp && ` • ${message.createdAt.format('MMM D, h:mm A')}`}
            {message.rcs_message && ' • RCS'}
          </MetaRow>
        </Layout>
        {itShowFeedbackPanel && (
          <FeedbackPanel
            messageId={message.sent_message_id.toString()}
            feedback={itPanelFeedback}
            onSubmit={handleITFeedbackPanelSubmit}
            onClose={handleITFeedbackPanelClose}
            showBrandCenterBanner
            categories={itCategories}
          />
        )}
      </div>
    );
  }

  if (shouldShowShopperResponses) {
    return (
      <div>
        <Layout align="justify">
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--spacing-1)',
            }}
          >
            <FeedbackThumbs
              feedback={feedbackDisplay}
              disabled={isLocked}
              onThumbsUp={handleThumbsUp}
              onThumbsDown={handleThumbsDown}
            />
            {onOpenDetails && (
              <DetailsIconButton
                icon={IconSet.Book}
                onClick={onOpenDetails}
                aria-label="Message details"
                monochrome
                variant="text"
                size="small"
              />
            )}
          </span>
          <span>
            Sent by Shopper Responses
            {showTimestamp && ` • ${message.createdAt.format('MMM D, h:mm A')}`}
            {message.rcs_message && ' • RCS'}
          </span>
        </Layout>
        {showFeedbackPanel && (
          <FeedbackPanel
            messageId={message.sent_message_id.toString()}
            feedback={panelFeedback}
            onSubmit={handleFeedbackPanelSubmit}
            onClose={handleFeedbackPanelClose}
            showBrandCenterBanner
          />
        )}
      </div>
    );
  }

  if (isShopperAutomationMessage(message)) {
    return (
      <div>
        <Layout align="right">
          {(canSubmitFeedback || onOpenDetails) && (
            <span
              style={{
                marginRight: 'auto',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--spacing-1)',
              }}
            >
              {canSubmitFeedback && (
                <FeedbackThumbs
                  feedback={feedbackDisplay}
                  disabled={isLocked}
                  onThumbsUp={handleThumbsUp}
                  onThumbsDown={handleThumbsDown}
                />
              )}
              {onOpenDetails && (
                <DetailsIconButton
                  icon={IconSet.Book}
                  onClick={onOpenDetails}
                  aria-label="Message details"
                  monochrome
                  variant="text"
                  size="small"
                />
              )}
            </span>
          )}
          <span>
            {message.scheduled ? (
              <>
                Created by Sender
                {message.scheduledAt && (
                  <>
                    {' • '}
                    Predicted Send:{' '}
                    {moment
                      .utc(message.scheduledAt)
                      .local()
                      .format('MMM D, h:mm A')}
                  </>
                )}
              </>
            ) : (
              <>
                Sent by Shopper Automations
                {showTimestamp &&
                  ` • ${message.createdAt.format('MMM D, h:mm A')}`}
              </>
            )}
            {message.rcs_message && ' • RCS'}
          </span>
        </Layout>
        {canSubmitFeedback && showFeedbackPanel && (
          <FeedbackPanel
            messageId={message.sent_message_id.toString()}
            feedback={panelFeedback}
            onSubmit={handleFeedbackPanelSubmit}
            onClose={handleFeedbackPanelClose}
            showBrandCenterBanner
          />
        )}
      </div>
    );
  }

  const supportIntegrationLabel =
    message.direction === 'outgoing'
      ? getSupportIntegrationLabel(message.subtype)
      : undefined;

  if (supportIntegrationLabel) {
    return (
      <span>
        {`Sent via ${supportIntegrationLabel}`}
        {showTimestamp && ` • ${message.createdAt.format('MMM D, h:mm A')}`}
        {message.rcs_message && ' • RCS'}
      </span>
    );
  }

  if (showTimestamp) {
    const label = getOutboundSourceLabel(message);
    if (label) {
      const isLoading =
        (message.type === 'flow_action' && isFlowRouteLoading) ||
        (message.type === 'keyword' && isKeywordLoading);
      const flowName =
        message.type === 'flow_action' ? flowRouteData?.flow.name : undefined;
      const keywordText =
        message.type === 'keyword' ? keywordData?.keyword.keyword : undefined;
      const enrichedName = flowName ?? keywordText;
      const displayLabel = isLoading ? 'Loading...' : enrichedName ?? label;
      const flowMessageLabel = flowRoute?.isCampaignView
        ? 'Campaign message'
        : 'Flow message';
      return (
        <MetaRow>
          {sourceHref && (
            <ClickableIcon
              onClick={
                !isLoading
                  ? () =>
                      window.open(sourceHref, '_blank', 'noopener,noreferrer')
                  : undefined
              }
            >
              <Icon component={IconSet.ArrowUpRight} size="small" />
            </ClickableIcon>
          )}
          {sourceHref ? (
            <SourceLabel
              href={sourceHref}
              target="_blank"
              rel="noopener noreferrer"
              $isLoading={isLoading}
              onClick={handleSourceClick}
              title={enrichedName}
            >
              {displayLabel}
            </SourceLabel>
          ) : (
            <StaticLabel title={enrichedName}>{displayLabel}</StaticLabel>
          )}
          {flowName && <span>{flowMessageLabel}</span>}
          {keywordText && <span>Keyword message</span>}
          {' • '}
          <span>
            {message.createdAt.format('MMM D, h:mm A')}
            {message.rcs_message && ' • RCS'}
          </span>
        </MetaRow>
      );
    }
    return (
      <span>
        {message.createdAt.format('MMM D, h:mm A')}
        {message.rcs_message && ' • RCS'}
      </span>
    );
  }

  if (message.rcs_message) {
    return <span>RCS</span>;
  }

  return null;
};

const MessageMetadata = ({
  message,
  showTimestamp = true,
  subscriber,
  onOpenDetails,
}: MessageMetadataProps) => {
  return (
    <StyledMetadata
      messageType={message.type}
      incoming={message.direction === 'incoming'}
    >
      <MessageSource
        message={message}
        showTimestamp={showTimestamp}
        subscriber={subscriber}
        onOpenDetails={onOpenDetails}
      />
    </StyledMetadata>
  );
};

export default MessageMetadata;
